import useSWR, { type SWRConfiguration } from 'swr';
import denApi from '@/lib/api/denApi';
import type { Signature } from '@/types/signature';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import type {
  DomainInvalidReason,
  ProposalValidity,
} from '@mls-wallet/shared-types';
import { SIGNABLE_POLL_INTERVAL_MS } from '@/components/execution/execution-polling';

// FE-local types (avoid importing from @mls-wallet/database)
export type AdminUserProposal = {
  action: 'ADD' | 'REMOVE';
  userId: string;
};

export type AdminChangeProposal = {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  validity: ProposalValidity;
  domainInvalidReason?: DomainInvalidReason;
  organizationId: string;
  createdByUserId: string;
  salt: string;
  createdAt: string;
  updatedAt: string;
  adminUserProposals: AdminUserProposal[];
  // Nullable: null means "no change" (keep org's current value).
  numberOfApprovalsNeeded: number | null;
};

type ActiveAdminChangeProposalResponse = {
  proposal: AdminChangeProposal;
  isExecuting: boolean;
  approvals: Signature[];
  rejections: Signature[];
  requiredNetworkIds: number[];
  requiredApprovals: number;
  totalVoters: number;
} | null;

const fetcher = async (url: string) =>
  await denApi.get<ActiveAdminChangeProposalResponse, void>(url);

/**
 * Fetches the currently active (PENDING) admin change proposal for an organization.
 * Only one active proposal can exist at a time per organization.
 * Automatically polls every 2 seconds to keep cross-user state fresh.
 *
 * @param organizationId - The organization ID to fetch the active proposal for
 * @param swrOptions - Optional SWR configuration overrides
 * @returns The active proposal with approval/rejection signatures, or null if none exists
 */
export function useActiveAdminChangeProposal(
  swrOptions?: SWRConfiguration<ActiveAdminChangeProposalResponse>
) {
  const { organization } = useMyOrganization();
  const isAdmin = Boolean(organization?.isAdmin);
  // Org context comes from x-current-org-id cookie (not URL params)
  const url = isAdmin
    ? `admin/organization/admin-change-proposals/active`
    : null;

  const { data, error, isLoading, mutate } =
    useSWR<ActiveAdminChangeProposalResponse>(url, fetcher, {
      revalidateOnFocus: false,
      refreshInterval: SIGNABLE_POLL_INTERVAL_MS,
      ...(swrOptions ?? {}),
    });

  return {
    activeProposal: data ?? null,
    error,
    isLoading,
    refresh: mutate,
  };
}
